// ChatCommandPalette — in-chat ⌘K palette so the user can search and
// trigger any cross-app action without leaving the chat shell.
//
// Sections (in order):
//  1. Quick actions     — always shown, scoped to the current chat
//                         (browse library, recent chats, save playbook,
//                         new chat, generate MVP, settings, account).
//  2. AI tools          — live filter over the published `tools` table,
//                         click opens the tool's external URL, ⌘+enter
//                         seeds the composer with a prompt about it.
//  3. Library entries   — live filter over public Library entries, click
//                         opens /library/:id, ⌘+enter forks into chat
//                         (stashChatSeed → reload home with seed).
//  4. People            — only shown when the query is ≥ 2 chars; queries
//                         the public profiles table by username /
//                         display_name; clicking opens /profile/:username.
//
// Why one component instead of a route: the user's whole task lives in
// the chat shell, so navigating away to a directory just to find a tool
// or a profile fights the chat-first model. Palette = inline jump.
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Wrench,
  Library as LibraryIcon,
  History,
  BookmarkPlus,
  MessageSquare,
  Sparkles,
  Settings as SettingsIcon,
  UserCircle2,
  ExternalLink,
  GitFork,
  ArrowRight,
  Users,
  Rocket,
} from "lucide-react";
import { useTools } from "@/hooks/useTools";
import { useLibraryEntries, type LibraryEntry } from "@/hooks/useLibrary";
import { supabase } from "@/integrations/supabase/client";
import { stashChatSeed } from "@/lib/chatSeedBus";
import { toast } from "sonner";
import { buildAcceleratorSeed } from "@/data/acceleratorPlaybook";

interface PaletteProfile {
  user_id: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
}

export interface ChatCommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Open the in-chat Library dialog, optionally on the recent tab. */
  onOpenLibrary: (tab: "library" | "recent", query?: string) => void;
  /** Open the MVP generator with the typed seed pre-filled. */
  onOpenMvp: (idea: string) => void;
  /** Open the Save-as-playbook dialog for the current transcript. */
  onSavePlaybook: () => void;
  /** Whether the current transcript has anything worth saving. */
  canSavePlaybook: boolean;
  /** Reset the chat shell to a blank conversation. */
  onNewChat: () => void;
  /** Seed the composer with the given text (e.g. fork-to-chat handoff). */
  onSeedComposer: (text: string) => void;
}

/** Truncate text for command-list rows so very long titles don't blow
 *  out the popover width. */
const trim = (s: string, n = 80) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

export default function ChatCommandPalette({
  open,
  onOpenChange,
  onOpenLibrary,
  onOpenMvp,
  onSavePlaybook,
  canSavePlaybook,
  onNewChat,
  onSeedComposer,
}: ChatCommandPaletteProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const { tools } = useTools();

  // Library + profiles only fetch once the palette is opened so we don't
  // pay the network cost on every chat-shell render.
  const { data: libraryEntries = [] } = useLibraryEntries(
    open ? { sort: "trending", search: query.trim() } : { sort: "trending" },
  );

  // Lightweight profile search — debounced inline since the palette
  // already debounces user input via React's render loop being cheap.
  const [profiles, setProfiles] = useState<PaletteProfile[]>([]);
  useEffect(() => {
    if (!open) return;
    const term = query.trim();
    if (term.length < 2) {
      setProfiles([]);
      return;
    }
    let cancelled = false;
    const t = window.setTimeout(async () => {
      try {
        const like = `%${term}%`;
        const { data } = await supabase
          // public_profiles view already filters to is_public = true and
          // strips phone columns; the explicit is_public eq below stays
          // for clarity but is now redundant.
          .from("public_profiles")
          .select("user_id, username, display_name, avatar_url")
          .eq("is_public", true)
          .or(`username.ilike.${like},display_name.ilike.${like}`)
          .limit(8);
        if (!cancelled) setProfiles((data ?? []) as PaletteProfile[]);
      } catch {
        if (!cancelled) setProfiles([]);
      }
    }, 180);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [open, query]);

  // Reset query whenever the palette closes so opening it again is a
  // clean slate (prevents "why is this still showing my last search?").
  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  // Keep the palette tools list bounded — the catalog can have hundreds
  // of entries and rendering them all into a popover would jank.
  const filteredTools = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? tools.filter((t) => {
          const hay = `${t.name} ${t.description} ${t.subcategory} ${(t.tags ?? []).join(" ")}`.toLowerCase();
          return hay.includes(q);
        })
      : tools.slice(0, 12);
    return list.slice(0, 12);
  }, [tools, query]);

  const close = () => onOpenChange(false);

  const handleToolOpen = (toolName: string, url: string) => {
    if (url) window.open(url, "_blank", "noopener,noreferrer");
    else toast.info(`No link on file for ${toolName}`);
    close();
  };

  const handleToolFork = (toolName: string, description: string) => {
    onSeedComposer(
      `Help me get the most out of ${toolName}. Context: ${description.trim() || "popular AI tool from the HackerLabs catalogue."} Walk me through a first useful workflow step by step.`,
    );
    toast.success(`Loaded ${toolName} into the composer`);
    close();
  };

  const handleEntryOpen = (entry: LibraryEntry) => {
    navigate(`/library/${entry.id}`);
    close();
  };

  const handleEntryFork = (entry: LibraryEntry) => {
    // Mirror ForkToChatButton's basic seed shape so the chat shell pops
    // a primer + draft on next mount. Route to the unified chat shell at
    // /workspace?tab=chat — Index ("/") doesn't mount UnifiedChatShell so
    // navigating there used to silently drop the seed.
    const lines = [`# ${entry.title}`, "", entry.summary || ""];
    stashChatSeed({
      primer: lines.join("\n").trim(),
      draft: `Help me apply this playbook to my situation. Start by asking me one clarifying question.`,
      source: "command-palette:fork-to-chat",
      entryId: entry.id,
      title: entry.title,
    });
    navigate("/workspace?tab=chat");
    toast.success(`Forked "${trim(entry.title, 40)}" into a new chat`);
    close();
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder="Search tools, library entries, people, or actions…"
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        <CommandEmpty>No matches. Try a different keyword.</CommandEmpty>

        <CommandGroup heading="Quick actions">
          <CommandItem
            value="action accelerator startup playbook"
            onSelect={() => {
              const { primer } = buildAcceleratorSeed();
              // Auto-send a clean kick-off prompt so the assistant
              // immediately runs Step 1 and asks for the founder's idea
              // — matches the "Start the Accelerator" CTA on /accelerator.
              stashChatSeed({
                primer,
                draft:
                  "Let's start the Accelerator. Run Step 1 — validate the idea — and ask me for my startup idea so we can begin.",
                source: "command-palette:accelerator",
                title: "Quest: Startup Accelerator",
                autoSend: true,
              });
              navigate("/workspace?tab=chat");
              toast.success("Loaded the Accelerator playbook into a new chat");
              close();
            }}
          >
            <Rocket className="w-4 h-4 mr-2 text-primary" />
            Start the Accelerator (10-step playbook)
          </CommandItem>
          <CommandItem
            value="action browse library"
            onSelect={() => {
              onOpenLibrary("library", query.trim());
              close();
            }}
          >
            <LibraryIcon className="w-4 h-4 mr-2" />
            Browse Library
          </CommandItem>
          <CommandItem
            value="action recent chats"
            onSelect={() => {
              onOpenLibrary("recent");
              close();
            }}
          >
            <History className="w-4 h-4 mr-2" />
            Recent chats
          </CommandItem>
          <CommandItem
            value="action save playbook"
            disabled={!canSavePlaybook}
            onSelect={() => {
              if (!canSavePlaybook) return;
              onSavePlaybook();
              close();
            }}
          >
            <BookmarkPlus className="w-4 h-4 mr-2" />
            Save current chat as playbook
          </CommandItem>
          <CommandItem
            value="action new chat"
            onSelect={() => {
              onNewChat();
              close();
            }}
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            New chat
          </CommandItem>
          <CommandItem
            value="action generate mvp"
            onSelect={() => {
              onOpenMvp(query.trim());
              close();
            }}
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Generate an MVP from idea
          </CommandItem>
          <CommandItem
            value="action settings"
            onSelect={() => {
              navigate("/settings");
              close();
            }}
          >
            <SettingsIcon className="w-4 h-4 mr-2" />
            Open Settings
          </CommandItem>
          <CommandItem
            value="action account"
            onSelect={() => {
              navigate("/account");
              close();
            }}
          >
            <UserCircle2 className="w-4 h-4 mr-2" />
            Account & billing
          </CommandItem>
        </CommandGroup>

        {filteredTools.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="AI tools">
              {filteredTools.map((tool) => (
                <CommandItem
                  key={`tool-${tool.name}`}
                  value={`tool ${tool.name} ${tool.subcategory} ${(tool.tags ?? []).join(" ")}`}
                  onSelect={() => handleToolOpen(tool.name, tool.url)}
                  className="group"
                >
                  <Wrench className="w-4 h-4 mr-2 text-muted-foreground" />
                  <div className="flex flex-col min-w-0 flex-1">
                    <span className="truncate text-xs">{tool.name}</span>
                    <span className="truncate text-[10px] text-muted-foreground">
                      {trim(tool.description || tool.subcategory || "", 70)}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToolFork(tool.name, tool.description);
                    }}
                    className="ml-2 inline-flex items-center gap-1 text-[10px] text-muted-foreground hover:text-primary transition-colors px-1.5 py-0.5 rounded border border-transparent hover:border-primary/40"
                    title="Send a primer about this tool to the composer"
                  >
                    <GitFork className="w-3 h-3" />
                    Use in chat
                  </button>
                  <ExternalLink className="w-3 h-3 ml-2 text-muted-foreground/60 shrink-0" />
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {libraryEntries.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Library">
              {libraryEntries.slice(0, 8).map((entry) => (
                <CommandItem
                  key={`lib-${entry.id}`}
                  value={`library ${entry.title} ${entry.tags.join(" ")} ${entry.kind}`}
                  onSelect={() => handleEntryOpen(entry)}
                  className="group"
                >
                  <LibraryIcon className="w-4 h-4 mr-2 text-muted-foreground" />
                  <div className="flex flex-col min-w-0 flex-1">
                    <span className="truncate text-xs">{entry.title}</span>
                    <span className="truncate text-[10px] text-muted-foreground">
                      {entry.kind} · {entry.fork_count} fork{entry.fork_count === 1 ? "" : "s"}
                      {entry.summary ? ` · ${trim(entry.summary, 60)}` : ""}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEntryFork(entry);
                    }}
                    className="ml-2 inline-flex items-center gap-1 text-[10px] text-muted-foreground hover:text-primary transition-colors px-1.5 py-0.5 rounded border border-transparent hover:border-primary/40"
                    title="Fork this entry into a fresh chat"
                  >
                    <GitFork className="w-3 h-3" />
                    Fork
                  </button>
                  <ArrowRight className="w-3 h-3 ml-2 text-muted-foreground/60 shrink-0" />
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {profiles.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="People">
              {profiles.map((p) => (
                <CommandItem
                  key={`profile-${p.user_id}`}
                  value={`profile ${p.username} ${p.display_name ?? ""}`}
                  onSelect={() => {
                    navigate(`/profile/${p.username}`);
                    close();
                  }}
                >
                  <Users className="w-4 h-4 mr-2 text-muted-foreground" />
                  <div className="flex flex-col min-w-0 flex-1">
                    <span className="truncate text-xs">
                      {p.display_name || p.username}
                    </span>
                    <span className="truncate text-[10px] text-muted-foreground">
                      @{p.username}
                    </span>
                  </div>
                  <ArrowRight className="w-3 h-3 ml-2 text-muted-foreground/60 shrink-0" />
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}
